/**
 * Badge visual para indicar origem de mensagens Instagram Direct
 * Exibe ícone e texto identificando o tipo específico da mensagem:
 * - story_mention: Menção no Story
 * - story_reply: Resposta ao Story  
 * - reel_share: Reel Compartilhado
 * - share: Compartilhamento
 * 
 * @version 1.0.0
 */
import { cn } from "@/lib/utils";

// Tipos de mensagem Instagram que possuem badge
type InstagramMessageType = 'story_mention' | 'story_reply' | 'reel_share' | 'share';

interface InstagramSourceBadgeProps {
  /** Tipo da mensagem Instagram */
  messageType: InstagramMessageType;
  /** Direção da mensagem para estilização */
  direction?: 'inbound' | 'outbound';
  /** Classes CSS adicionais */
  className?: string;
}

// Configuração visual de cada tipo de badge
const BADGE_CONFIG: Record<InstagramMessageType, {
  icon: string;
  label: string;
  bgClass: string;
  textClass: string;
}> = {
  story_mention: {
    icon: "📸",
    label: "Menção no Story",
    bgClass: "bg-gradient-to-r from-purple-500/20 to-pink-500/20",
    textClass: "text-purple-600 dark:text-purple-400",
  },
  story_reply: {
    icon: "💬",
    label: "Resposta ao Story",
    bgClass: "bg-gradient-to-r from-purple-500/20 to-pink-500/20",
    textClass: "text-purple-600 dark:text-purple-400",
  },
  reel_share: {
    icon: "🎬",
    label: "Reel",
    bgClass: "bg-gradient-to-r from-red-500/20 to-pink-500/20",
    textClass: "text-red-600 dark:text-red-400",
  },
  share: {
    icon: "🔗",
    label: "Compartilhamento",
    bgClass: "bg-blue-500/20",
    textClass: "text-blue-600 dark:text-blue-400",
  },
};

/**
 * Badge visual para identificar origem de mensagens Instagram
 * Renderiza um chip estilizado com ícone e texto
 */
export function InstagramSourceBadge({ 
  messageType, 
  direction = 'inbound',
  className 
}: InstagramSourceBadgeProps) {
  const config = BADGE_CONFIG[messageType];
  
  if (!config) return null;

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mb-1",
        config.bgClass,
        config.textClass,
        className
      )}
    >
      <span>{config.icon}</span>
      <span>{config.label}</span>
    </div>
  );
}

/**
 * Verifica se o tipo de mensagem é um tipo especial do Instagram
 * que deve exibir o badge de origem
 */
export function isInstagramSpecialType(messageType: string): messageType is InstagramMessageType {
  return ['story_mention', 'story_reply', 'reel_share', 'share'].includes(messageType);
}

export default InstagramSourceBadge;
